/**
 * Traduccion de errores de Supabase Auth al español.
 *
 * Por que existe: GoTrue devuelve sus mensajes en ingles ("Password should be
 * at least 6 characters", "Invalid login credentials"...). Cada pantalla tenia
 * su propio mapeo parcial y, cuando el mensaje no matcheaba, se mostraba el
 * texto crudo en ingles en medio de una UI en español. El caso concreto que lo
 * destapo: el mapeo viejo buscaba la palabra "short", que Supabase no usa.
 *
 * Regla: NUNCA mostrar `error.message` de Supabase directo en la UI. Todo
 * mensaje de auth pasa por `traducirErrorAuth`.
 */

// El fallback nunca es el mensaje crudo: si aparece algo que no conocemos,
// preferimos un texto generico en español antes que ingles en pantalla.
const FALLBACK = 'Ocurrió un error inesperado. Intentá nuevamente.';

// Orden importante: las reglas mas especificas van primero.
const REGLAS = [
  // --- Contraseña ---
  {
    test: (m) => /password/.test(m) && /(at least|should be|too short|short|minimum|min length|6 characters|weak)/.test(m),
    msg: 'La contraseña debe tener al menos 6 caracteres.',
  },
  {
    test: (m) => /password/.test(m) && /(different from|should be different|same as)/.test(m),
    msg: 'La nueva contraseña tiene que ser distinta a la anterior.',
  },
  {
    test: (m) => /(pwned|compromised|breach)/.test(m),
    msg: 'Esa contraseña aparece en filtraciones conocidas. Elegí otra.',
  },

  // --- Credenciales ---
  {
    test: (m) => /invalid login credentials/.test(m) || (/invalid/.test(m) && /credential/.test(m)),
    msg: 'El correo o la contraseña no coinciden. Revisá los datos e intentá nuevamente.',
  },
  {
    test: (m) => /(email not confirmed|not confirmed|email_not_confirmed)/.test(m),
    msg: 'La cuenta todavía no está confirmada. Revisá tu correo para activarla.',
  },
  {
    test: (m) => /(user not found|no user found)/.test(m),
    msg: 'No encontramos una cuenta con ese email.',
  },

  // --- Email ---
  {
    test: (m) => /(already registered|user already|already been registered|already exists)/.test(m),
    msg: 'Este email ya está registrado. ¿Querés iniciar sesión?',
  },
  {
    test: (m) => /email/.test(m) && /(invalid|not valid|bad format)/.test(m),
    msg: 'El email ingresado no es válido.',
  },
  {
    test: (m) => /email address .* is invalid/.test(m),
    msg: 'El email ingresado no es válido.',
  },
  {
    test: (m) => /(signups not allowed|signup is disabled|email signups are disabled)/.test(m),
    msg: 'El registro con email está deshabilitado. Escribinos y te damos de alta.',
  },

  // --- Rate limit / red / sesion ---
  {
    test: (m) => /(rate limit|too many|429)/.test(m),
    msg: 'Se hicieron varios intentos seguidos. Esperá unos minutos antes de volver a probar.',
  },
  {
    test: (m) => /(failed to fetch|network|networkerror|timeout|econnrefused)/.test(m),
    msg: 'No fue posible comunicarse con el servidor. Revisá la conexión e intentá nuevamente.',
  },
  {
    test: (m) => /(token has expired|otp_expired|expired|invalid token)/.test(m),
    msg: 'El link expiró o ya fue usado. Pedí uno nuevo.',
  },
  {
    test: (m) => /(session|jwt)/.test(m) && /(missing|expired|not found)/.test(m),
    msg: 'Tu sesión expiró. Volvé a iniciar sesión.',
  },
  {
    test: (m) => /captcha/.test(m),
    msg: 'No pudimos validar el captcha. Recargá la página e intentá de nuevo.',
  },
];

/**
 * @param {string|{message?:string,status?:number}} error mensaje o error de Supabase
 * @param {string} [fallback] texto a mostrar si no reconocemos el error
 * @returns {string} mensaje en español, listo para la UI
 */
export function traducirErrorAuth(error, fallback = FALLBACK) {
  const raw = typeof error === 'string' ? error : (error?.message || '');
  const m = String(raw).toLowerCase();
  if (!m) return fallback;

  for (const regla of REGLAS) {
    if (regla.test(m)) return regla.msg;
  }

  // 422 de GoTrue es, casi siempre, la contraseña que no cumple los requisitos.
  if (typeof error === 'object' && error?.status === 422) {
    return 'La contraseña no cumple los requisitos o es igual a la anterior.';
  }

  // Si el mensaje ya vino en español (lo tiramos nosotros), lo respetamos.
  if (/[áéíóúñ¿¡]/i.test(raw)) return raw;

  return fallback;
}

export default traducirErrorAuth;
